import { useRef, useState, useEffect, type FunctionComponent } from 'react';
import styled from 'styled-components';
import { PersonalSetting, ViewIds } from '@ardoq/api-types';
import { map } from 'rxjs';
import { connect } from '@ardoq/rxbeach';
import { viewpointModeViewModel$ } from './viewModel$/viewpointModeViewModel$';
import { ViewpointModeComponentMatrixViewProperties } from './types';
import { getComponentMatrix2EmptyState } from './getComponentMatrix2EmptyState';
import {
  COMPONENT_MATRIX_2_COMMANDS,
  PRESENTATION_MODE_COMPONENT_MATRIX_2_COMMANDS,
} from './commands';
import { DisabledZoomControls } from '@ardoq/view-settings';
import {
  ComponentMatrixViewContainer,
  ComponentMatrixBodyContainer,
} from './view/atoms';
import { componentMatrix as ComponentMatrix } from './view/componentMatrix';
import { Team } from '@ardoq/profiling';
import { usePerformanceTracking } from 'tabview/graphComponent/usePerformanceTracking';
import { ComponentMatrixViewLegend } from '../ComponentMatrixViewLegend';
import { EmptyState2 as EmptyStateBase } from './EmptyState2';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { noop } from 'lodash';
import { returnNull } from '@ardoq/common-helpers';
import { isPresentationMode } from 'env';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { recordViewScrollbarWidth } from 'tabview/recordViewScrollbarWidth';

const VIEW_ID = ViewIds.COMPONENT_MATRIX_2;

const ContentContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  &.forExport {
    overflow: visible;
    width: fit-content;
    flex: unset;
  }
`;

const ComponentMatrix2View = ({
  commands,
  viewStatusReporter,
  dataWorkToken,
  ...viewModelProperties
}: ViewpointModeComponentMatrixViewProperties) => {
  const {
    viewModel,
    viewSettings,
    viewSettingsState,
    hasReferences,
    hasFilteredNullItems,
    errors,
    errorInfoBoxLabels,
    i18nLocale,
    contextComponentId,
    config,
  } = viewModelProperties;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    commands.updateViewProperties(viewModelProperties);
    commands.updateViewContext({
      getContainer: () => containerRef.current,
      getGraphComponent: returnNull,
      optimizeLayout: null,
      zoomIn: null,
      zoomOut: null,
      zoomControls: null,
      zoomControlsDisabledState: DisabledZoomControls.NONE,
      toggleLegend: () =>
        commands.updateViewLegendSettings(!viewSettings.isLegendActive),
      isLegendActive: viewSettings.isLegendActive,
    });
  }); // no dep array — runs every render, matching how bubble chart works

  useEffect(() => {
    if (dataWorkToken) {
      viewStatusReporter.reportWorkFinished(dataWorkToken);
    }
  }, [viewStatusReporter, dataWorkToken]);

  const [clearedErrors, setClearedErrors] = useState(false);
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_COMPONENT_MATRIX_2
  );

  const {
    noVisibleComponents,
    noReferencesOrListFields,
    isEmpty: isEmptyView,
  } = getComponentMatrix2EmptyState({
    itemCount: viewModel.items.length,
    hasReferences,
    viewSettingsState,
  });

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const { current: container } = containerRef;
    const body = container.querySelector<HTMLElement>(
      `${ComponentMatrixBodyContainer}`
    );
    if (!body) {
      return;
    }
    const observer = recordViewScrollbarWidth(container, body);
    return () => observer.disconnect();
  }, [containerRef]);

  return (
    <ComponentMatrixViewContainer
      className={`tab-pane ${VIEW_ID}Tab active react-view`}
      data-context-menu={OBJECT_CONTEXT_MENU_NAME}
    >
      {isEmptyView ? (
        <EmptyStateBase
          noVisibleComponents={noVisibleComponents}
          noReferencesOrListFields={noReferencesOrListFields}
          hasFilteredNullItems={hasFilteredNullItems}
          allDescendantsIncluded={true}
        />
      ) : (
        <ContentContainer ref={containerRef}>
          <ComponentMatrix
            viewModel={viewModel}
            i18nLocale={i18nLocale}
            contextComponentId={contextComponentId}
            hideRowHeadersResizeHandle={config.hideRowHeadersResizeHandle}
            commands={commands}
          />
          <ComponentMatrixViewLegend
            legendComponentTypes={viewModel.legendComponentTypes}
            isLegendActive={viewSettings.isLegendActive}
            i18nLocale={i18nLocale}
          />
          <ErrorInfoBox
            errors={clearedErrors ? [] : errors}
            hasClones={false}
            clearHasClones={noop}
            isShowNeverAgainSet={neverShowAgain}
            isPresentationMode={config.isPresentationMode}
            clearErrors={() => setClearedErrors(true)}
            toggleNeverShowAgain={toggleNeverShowAgain}
            {...errorInfoBoxLabels}
          />
        </ContentContainer>
      )}
    </ComponentMatrixViewContainer>
  );
};

const PerformanceTrackedComponentMatrix2 = (
  props: ViewpointModeComponentMatrixViewProperties
) => {
  usePerformanceTracking('component matrix 2 view render', 1000, {
    team: Team.INSIGHT,
    viewId: VIEW_ID,
    metadata: { itemCount: props.viewModel.items.length },
  });

  return <ComponentMatrix2View {...props} />;
};

const toComponentMatrix2Commands = () =>
  isPresentationMode()
    ? PRESENTATION_MODE_COMPONENT_MATRIX_2_COMMANDS
    : COMPONENT_MATRIX_2_COMMANDS;

const ConnectedComponentMatrix2: FunctionComponent = connect(
  PerformanceTrackedComponentMatrix2,
  viewpointModeViewModel$.pipe(
    map(viewModel => ({ ...viewModel, commands: toComponentMatrix2Commands() }))
  )
);

export { ComponentMatrix2View, ConnectedComponentMatrix2 };
